// components
import PageTitle from '../PageTitle';
import { ReactComponent as CloseIcon } from '../../../assets/icons/error-icon.svg';
import Search from './Search';

// libraries
import React from 'react';
import axios from 'axios';
import { debounce } from 'lodash';

// styles
import {
  Wrapper,
  HeadingRow,
  HashtagsContainer,
  Hashtag,
  ClearButton,
  ShowLessButton,
  Categories,
} from './styles';
import { toNumber } from 'lodash';

interface IProps {
  tagsUrl: string;
  selectedTags: number[];
  setSelectedTags: React.Dispatch<React.SetStateAction<number[]>>;
  pageTitle?: string;
  categories?: {
    id: number;
    type: string;
    title: string;
  }[];
  setCategory?: React.Dispatch<React.SetStateAction<string>>;
  activeCategory?: string | null;
}

export const Hashtags = ({
  tagsUrl,
  selectedTags,
  setSelectedTags,
  pageTitle,
  categories,
  setCategory,
  activeCategory,
}: IProps) => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const [tags, setTags] = React.useState<
    { [key: number]: { id: number; title: string } } | {}
  >({});
  const hashtagsRef = React.useRef(null);
  const fetchTags = () => {
    axios
      .get(baseUrl + tagsUrl)
      .then(response => {
        setTags(response.data);
        placeShowModeButton(hashtagsRef.current);
      })
      .catch(error => console.error(`Error: ${error}`));
  };

  // set initial state
  React.useEffect(() => {
    fetchTags();

    window.addEventListener(
      'resize',
      debounce(() => placeShowModeButton(hashtagsRef.current), 100),
    );

    return () => {
      window.removeEventListener(
        'resize',
        debounce(() => placeShowModeButton(hashtagsRef.current), 100),
      );
    };
  }, []);

  React.useEffect(() => {
    placeShowModeButton(hashtagsRef.current);
  }, [selectedTags, tags]);

  const placeShowModeButton = (container: HTMLElement | null) => {
    if (container !== null && !container.classList.contains('opened')) {
      let oldLastElement = container.querySelector('.show-more');
      if (oldLastElement) {
        oldLastElement.removeEventListener('click', openHashtagsContainer);
        oldLastElement.remove();
      }

      let tags = container.querySelectorAll(':scope > *');
      let bottomPosition = container.offsetTop + container.offsetHeight;
      let showMoreElement: HTMLElement | null = null;

      for (let i = 0; i < tags.length; i++) {
        if ((tags[i] as HTMLElement).offsetTop >= bottomPosition && i > 1) {
          let preLastElement = tags[i - 1] as HTMLElement;

          showMoreElement = document.createElement('p');
          showMoreElement.innerText = '...';
          showMoreElement.classList.add('show-more');
          container.insertBefore(showMoreElement, preLastElement);

          showMoreElement.style.marginRight =
            container.offsetLeft +
            container.offsetWidth -
            (showMoreElement.offsetLeft + showMoreElement.offsetWidth) -
            8 +
            'px';
          break;
        }
      }
      showMoreElement?.addEventListener('click', openHashtagsContainer);
    }
  };

  const openHashtagsContainer = () => {
    let container: HTMLElement | null = hashtagsRef.current;
    if (container !== null) {
      (container as HTMLElement).classList.add('opened');
    }
  };
  const closeHashtagsContainer = () => {
    let container: HTMLElement | null = hashtagsRef.current;
    if (container !== null) {
      (container as HTMLElement).classList.remove('opened');
      placeShowModeButton(container);
    }
  };

  const selectTag = (id: number) => {
    setSelectedTags(prevState => {
      if (!prevState.includes(id)) {
        let result = prevState.concat(id);
        result.sort(function (a, b) {
          return a - b;
        });
        return result;
      } else return prevState;
    });
  };
  const deselectTag = (id: number) => {
    setSelectedTags(prevState => {
      if (prevState.includes(id)) prevState.splice(prevState.indexOf(id), 1);
      return [...prevState];
    });
  };

  return (
    <Wrapper>
      <HeadingRow
        className={`${
          categories && setCategory && activeCategory && 'with-categories'
        }`}
      >
        <PageTitle>{pageTitle}</PageTitle>
        <Search
          allTags={tags}
          selectedTags={selectedTags}
          selectTag={selectTag}
        />
        {categories && setCategory && activeCategory && (
          <Categories>
            {categories.map(category => {
              return (
                <button
                  key={category.id}
                  className={`${activeCategory === category.type && 'active'}`}
                  onClick={() => setCategory(category.type)}
                >
                  {category.title}
                </button>
              );
            })}
          </Categories>
        )}
      </HeadingRow>
      <HashtagsContainer ref={hashtagsRef}>
        {selectedTags.map(id => {
          if (tags[id]) {
            return (
              <Hashtag
                key={id}
                isSelected={true}
                onClick={() => deselectTag(toNumber(id))}
              >
                {tags[id].title}
                <CloseIcon />
              </Hashtag>
            );
          }
        })}

        {Object.keys(tags).map(id => {
          if (!selectedTags.includes(toNumber(id))) {
            return (
              <Hashtag
                key={id}
                isSelected={false}
                onClick={() => selectTag(toNumber(id))}
              >
                {tags[id].title}
              </Hashtag>
            );
          }
        })}

        {Object.keys(tags).length > 0 && (
          <>
            <ClearButton
              onClick={() => {
                if (selectedTags.length > 0) setSelectedTags([]);
              }}
            >
              Очистити
            </ClearButton>
            <ShowLessButton onClick={() => closeHashtagsContainer()}>
              Згорнути
            </ShowLessButton>
          </>
        )}
      </HashtagsContainer>
    </Wrapper>
  );
};

export default Hashtags;
